/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: CSM.ROLE.LIST
 *
 * Role maintenance with edit drawer that handles:
 *  - Translation rows in csm_role_master_tl (display_text + description per locale)
 *  - The csm_role_page_access matrix - one row per page, with can_view / can_add /
 *    can_change / can_delete / can_signoff booleans.
 *
 * System roles (is_system='Y') are read-only for code/active status; they
 * may still have their access matrix and translations updated.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { useActiveLocale } from '../../lib/use_active_locale';
import { buildTranslationsGroup } from '../../components/common/translations_group';
import { routedInsert, routedUpdate, routedDelete, routedInsertMany } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { SortableHeader } from '../../components/common/SortableHeader';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { compareStrings, useSort } from '../../lib/use_sort';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import { FieldRenderer, toUldCoreLayout, type FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { TranslationsEditor } from '../../components/common/TranslationsEditor';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { hasAccess } from '../../types/auth';
import { registerChildEditor, type ChildEditorProps } from '../../lib/child_editors';
import { useRecordExtensions } from '../../hooks/use_record_extensions';
import { useRecordExtensionForm } from '../../hooks/use_record_extension_form';
import { ExtensionFacets } from '../../components/common/ExtensionFacets';
import { RecordExtensionFields } from '../../components/common/RecordExtensionFields';

const PAGE_CODE = 'CSM.ROLE.LIST';

type RoleSortKey = 'internal_code' | 'display_text' | 'is_system' | 'is_active';

interface RoleRow {
  role_id: number;
  internal_code: string;
  is_system: 'Y' | 'N';
  is_active: 'Y' | 'N';
  display_text: string;
  /** Every locale's text, kept so the related panel can list them (§17.B14). */
  tl: { locale_code: string; display_text: string }[];
}

interface PageRow {
  page_id: number;
  internal_code: string;
  display_text: string;
  parent_page_id: number | null;
  sort_order: number;
  /** Which application this page belongs to — used to group the access matrix by
   *  application (the platform's own app first, then any external systems whose
   *  pages are non-navigable "functions"). Null falls back to the default app. */
  application_id: number | null;
  /** A page with no route is an atomic "function" (an action in an external
   *  system) rather than a CRUD screen: it grants on/off, not the full matrix. */
  route_path: string | null;
}

interface AppInfo { application_id: number; label: string; is_default: boolean; }

/** A page is an atomic function (on/off) when it has no navigable route. CRUD
 *  screens always carry a route_path; external actions parked in the page catalog
 *  do not. This keeps the distinction generic — no app-specific table needed. */
const isFunctionPage = (p: PageRow): boolean => p.route_path == null;

type FlagKey = 'can_view' | 'can_add' | 'can_change' | 'can_delete' | 'can_signoff' | 'can_personalize_role' | 'can_personalize_user';
const FLAG_KEYS: readonly FlagKey[] = ['can_view', 'can_add', 'can_change', 'can_delete', 'can_signoff', 'can_personalize_role', 'can_personalize_user'];

interface PageNode { page: PageRow; children: PageNode[]; }

// Build the same parent_page_id hierarchy the navigation menu uses, so the Page
// Access editor is grouped identically to Pages and stays in sync automatically.
function buildPageTree(pages: PageRow[]): PageNode[] {
  const byId = new Map<number, PageNode>();
  for (const p of pages) byId.set(p.page_id, { page: p, children: [] });
  const roots: PageNode[] = [];
  for (const node of byId.values()) {
    const pid = node.page.parent_page_id;
    const parent = pid != null ? byId.get(pid) : undefined;
    if (parent) parent.children.push(node); else roots.push(node);
  }
  const sortFn = (a: PageNode, b: PageNode) =>
    (a.page.sort_order - b.page.sort_order) || a.page.display_text.localeCompare(b.page.display_text);
  const sortRec = (ns: PageNode[]) => { ns.sort(sortFn); ns.forEach(n => sortRec(n.children)); };
  sortRec(roots);
  return roots;
}

// every page_id in a subtree, including the node itself
function subtreeIds(node: PageNode): number[] {
  const out = [node.page.page_id];
  for (const c of node.children) out.push(...subtreeIds(c));
  return out;
}

interface AccessRow {
  page_id: number;
  can_view: boolean;
  can_add: boolean;
  can_change: boolean;
  can_delete: boolean;
  can_signoff: boolean;
  can_personalize_role: boolean;
  can_personalize_user: boolean;
}

interface TranslationRow {
  locale_code: string;
  display_text: string;
  description: string;
}

// ── Related-children (expand/contract detail panel) row shapes ────────────────
// A role is a PARENT of: the users assigned to it, the pages it grants access
// to, and the job descriptions that reference it. Names are resolved through the
// active locale's _tl; raw ids/codes never become the card label (§17.B4).
interface RelatedUserRow {
  user_id: number;
  name: string;   // display_name (card label)
  email: string;  // hover title
}
interface RelatedPageRow {
  page_id: number;
  name: string;   // localized page name (card label)
  code: string;   // internal_code (hover title)
  perms: string[]; // compact V/A/C/D/S summary
}
interface RelatedJobRow {
  job_id: number;
  name: string;   // localized job name (card label)
  code: string;   // internal_code (hover title)
}

/** Permission-badge hover labels for the Pages-with-access card. */
const PERM_TITLES: Record<string, string> = { V: 'View', A: 'Add', C: 'Change', D: 'Delete', S: 'Sign-off' };

const FILTER_KEY = 'csm.roles.filter';

const GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Code',     defaultWidth: 180 },
  { key: 'display_text',  header: 'Name',     defaultWidth: 220 },
  { key: 'is_system',     header: 'System',   defaultWidth: 90  },
  { key: 'is_active',     header: 'Active',   defaultWidth: 90  },
];

// ULD field layout for the Role edit modal's scalar header section.
const ROLE_EDIT_FIELD_DEFS: FieldDef[] = [
  { key: 'internal_code', type: 'text',    label: 'Internal Code', required: true, col_span: 1 },
  { key: 'is_active',     type: 'boolean', label: 'Active',                        col_span: 1 },
];
const ROLE_EDIT_CORE_LAYOUT = toUldCoreLayout(ROLE_EDIT_FIELD_DEFS);

registerUdcHost('CSM.ROLE.LIST', 'csm_role_master', 'Roles');

export interface RoleListPageProps {
  session: AuthSession;
}

export function RoleListPage({ session }: RoleListPageProps) {
  return (
    <PersonalizationProvider pageCode="CSM.ROLE.LIST" session={session}>
      <RoleListPageInner session={session} />
    </PersonalizationProvider>
  );
}

function RoleListPageInner({ session }: RoleListPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  // §8: resolve _tl data against the ACTIVE locale (follows a runtime switch), not
  // the stored preferred_locale (stale in memory until re-login).
  const activeLocale = useActiveLocale();
  const { isPersonalizing } = usePersonalization();
  const [roles, setRoles]       = useState<RoleRow[]>([]);
  // Related-children (expand/contract detail panel) - keyed by role_id.
  const detail = useExpandedRows<number>();
  const [usersByRole, setUsersByRole] = useState<Map<number, RelatedUserRow[]>>(new Map());
  const [pagesByRole, setPagesByRole] = useState<Map<number, RelatedPageRow[]>>(new Map());
  const [jobsByRole, setJobsByRole]   = useState<Map<number, RelatedJobRow[]>>(new Map());
  const [filter, setFilter]     = useState(() => sessionStorage.getItem(FILTER_KEY) ?? '');
  // §17.B5: per-column multi-value facets (system, active).
  const [facets, setFacets]     = useState<{ system: string[]; active: string[] }>({ system: [], active: ['Y'] });
  const sort = useSort<RoleSortKey>('csm.roles.sort', { key: 'internal_code', dir: 'asc' });
  const [editing, setEditing]   = useState<RoleRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState<string | null>(null);
  // Change 4: delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<RoleRow | null>(null);

  useEffect(() => { sessionStorage.setItem(FILTER_KEY, filter); }, [filter]);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const locale = activeLocale;
      const { data, error: e } = await apiDb
        .from('csm_role_master')
        .select(`
          role_id, internal_code, is_system, is_active,
          csm_role_master_tl ( locale_code, display_text )
        `)
        .order('internal_code');
      if (e) throw e;

      type RoleDataRow = { role_id: number; internal_code: string; is_system: 'Y' | 'N'; is_active: 'Y' | 'N'; csm_role_master_tl?: TlRow[] };
      const rows: RoleRow[] = (data ?? []).map((r: RoleDataRow) => {
        const tls: { locale_code: string; display_text: string }[] = r.csm_role_master_tl ?? [];
        const exact = tls.find(t => t.locale_code === locale);
        const fallback = tls.find(t => t.locale_code === 'EN_US') ?? tls[0];
        return {
          role_id: r.role_id,
          internal_code: r.internal_code,
          is_system: r.is_system,
          is_active: r.is_active,
          display_text: exact?.display_text ?? fallback?.display_text ?? r.internal_code,
          tl: tls,
        };
      });
      setRoles(rows);

      // ── Related children for the expand/contract detail panel ───────────────
      // A role's users (current assignments), the pages it grants, and the job
      // descriptions that reference it. Fetch in parallel, then resolve every
      // display name through its _tl against the active locale (EN_US fallback).
      const today = new Date().toISOString().slice(0, 10);
      const [uraRes, umRes, rpaRes, pageRes, jdrRes, jobRes] = await Promise.all([
        apiDb.from('csm_user_role_assignment')
          .select('role_id, user_id, effective_to')
          .or(`effective_to.is.null,effective_to.gte.${today}`),
        apiDb.from('csm_user_master').select('user_id, display_name, email'),
        apiDb.from('csm_role_page_access')
          .select('role_id, page_id, can_view, can_add, can_change, can_delete, can_signoff'),
        apiDb.from('csm_page_master')
          .select('page_id, internal_code, csm_page_master_tl ( locale_code, display_text )'),
        apiDb.from('csm_job_description_role').select('role_id, job_description_id'),
        apiDb.from('csm_job_description')
          .select('job_description_id, internal_code, csm_job_description_tl ( locale_code, display_text )'),
      ]);

      type Tl = { locale_code: string; display_text: string };
      const pickTl = (tls: Tl[] | undefined, code: string) => {
        const list = tls ?? [];
        return list.find(x => x.locale_code === locale)?.display_text
          ?? list.find(x => x.locale_code === 'EN_US')?.display_text
          ?? list[0]?.display_text ?? code;
      };

      // Resolve lookups: user identity, localized page name, localized job name.
      type RawUser = { user_id: number; display_name: string | null; email: string | null };
      const userInfo = new Map<number, { name: string; email: string }>();
      for (const u of (umRes.data ?? []) as RawUser[]) {
        userInfo.set(u.user_id, { name: u.display_name ?? u.email ?? String(u.user_id), email: u.email ?? '' });
      }
      type RawPage = { page_id: number; internal_code: string; csm_page_master_tl?: Tl[] };
      const pageInfo = new Map<number, { code: string; name: string }>();
      for (const p of (pageRes.data ?? []) as RawPage[]) {
        pageInfo.set(p.page_id, { code: p.internal_code, name: pickTl(p.csm_page_master_tl, p.internal_code) });
      }
      type RawJob = { job_description_id: number; internal_code: string; csm_job_description_tl?: Tl[] };
      const jobInfo = new Map<number, { code: string; name: string }>();
      for (const j of (jobRes.data ?? []) as RawJob[]) {
        jobInfo.set(j.job_description_id, { code: j.internal_code, name: pickTl(j.csm_job_description_tl, j.internal_code) });
      }

      // Users assigned to each role.
      type RawUra = { role_id: number; user_id: number };
      const userMap = new Map<number, RelatedUserRow[]>();
      for (const a of (uraRes.data ?? []) as RawUra[]) {
        const info = userInfo.get(a.user_id);
        if (!info) continue;
        const arr = userMap.get(a.role_id) ?? [];
        arr.push({ user_id: a.user_id, name: info.name, email: info.email });
        userMap.set(a.role_id, arr);
      }

      // Pages each role grants access to (with a V/A/C/D/S perm summary).
      type RawRpa = { role_id: number; page_id: number; can_view: boolean; can_add: boolean; can_change: boolean; can_delete: boolean; can_signoff: boolean };
      const pageMap = new Map<number, RelatedPageRow[]>();
      for (const a of (rpaRes.data ?? []) as RawRpa[]) {
        const info = pageInfo.get(a.page_id);
        if (!info) continue;
        const perms: string[] = [];
        if (a.can_view) perms.push('V');
        if (a.can_add) perms.push('A');
        if (a.can_change) perms.push('C');
        if (a.can_delete) perms.push('D');
        if (a.can_signoff) perms.push('S');
        const arr = pageMap.get(a.role_id) ?? [];
        arr.push({ page_id: a.page_id, name: info.name, code: info.code, perms });
        pageMap.set(a.role_id, arr);
      }

      // Job descriptions referencing each role.
      type RawJdr = { role_id: number; job_description_id: number };
      const jobMap = new Map<number, RelatedJobRow[]>();
      for (const jr of (jdrRes.data ?? []) as RawJdr[]) {
        const info = jobInfo.get(jr.job_description_id);
        if (!info) continue;
        const arr = jobMap.get(jr.role_id) ?? [];
        arr.push({ job_id: jr.job_description_id, name: info.name, code: info.code });
        jobMap.set(jr.role_id, arr);
      }

      for (const list of userMap.values()) list.sort((x, y) => x.name.localeCompare(y.name));
      for (const list of pageMap.values()) list.sort((x, y) => x.name.localeCompare(y.name));
      for (const list of jobMap.values()) list.sort((x, y) => x.name.localeCompare(y.name));
      setUsersByRole(userMap);
      setPagesByRole(pageMap);
      setJobsByRole(jobMap);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // reload is re-created each render; re-run on mount and whenever the active
  // locale changes (so the grid's _tl display text follows a runtime switch).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload();   }, [activeLocale]);

  // Returns true on success, false on failure (error is set in state).
  const handleDelete = async (r: RoleRow): Promise<boolean> => {
    setBusy(true); setError(null);
    try {
      if (r.is_system === 'Y') {
        throw new Error(t('roles.list.errors.cannot_delete_system'));
      }
      const today = new Date().toISOString().slice(0, 10);
      const { count: assignCount, error: aErr } = await apiDb
        .from('csm_user_role_assignment')
        .select('user_role_assignment_id', { head: true, count: 'exact' })
        .eq('role_id', r.role_id)
        .or(`effective_to.is.null,effective_to.gte.${today}`);
      if (aErr) throw aErr;
      if ((assignCount ?? 0) > 0) {
        throw new Error(
          t('roles.list.errors.users_hold_role', { count: assignCount ?? 0 }),
        );
      }
      const { error: dErr } = await routedDelete(session, 'csm_role_master', 'role_id', r.role_id);
      if (dErr) throw dErr;
      await reload();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  // User-defined columns / facets an admin placed on this page (runtime, no code).
  const udcGrid = useUdcGrid('csm_role_master', 'CSM.ROLE.LIST', { pkColumn: 'role_id', rows: roles as unknown as Record<string, unknown>[] });
  // Record extensions: a derived app may hang a 1:1 sister table off csm_role_master
  // (inert here — BaseApp registers none).
  const ext = useRecordExtensions(PAGE_CODE, { pkColumn: 'role_id' });

  // §17.B5: static yes/no facet options (logical columns of the list).
  const facetOptions = useMemo(() => ({
    system: [{ value: 'Y', label: t('common.boolean.system') }, { value: 'N', label: t('common.boolean.custom') }],
    active: [{ value: 'Y', label: t('common.boolean.active') }, { value: 'N', label: t('common.boolean.inactive') }],
  }), [t]);

  const facetsActive = facets.system.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => {
    setFilter('');
    setFacets({ system: [], active: [] });
  };

  const filtered = useMemo(() => {
    const matchesFacets = (r: RoleRow) =>
      (facets.system.length === 0 || facets.system.includes(r.is_system)) &&
      (facets.active.length === 0 || facets.active.includes(r.is_active));
    const base = (!filter.trim()
      ? roles
      : roles.filter(r => {
          const q = filter.toLowerCase();
          return r.internal_code.toLowerCase().includes(q) ||
            r.display_text.toLowerCase().includes(q);
        })).filter(matchesFacets).filter(r => udcGrid.matchRow(r as unknown as Record<string, unknown>));
    return sort.sorted(base, (a, b, key) => {
      if (udcGrid.isUdcKey(key)) return udcGrid.compare(a as unknown as Record<string, unknown>, b as unknown as Record<string, unknown>, key);
      return compareStrings((a as unknown as Record<string, unknown>)[key], (b as unknown as Record<string, unknown>)[key]);
    });
  }, [roles, filter, facets, sort, udcGrid]);

  // ── Related-children detail panel (users / pages / job descriptions) ─────────
  const hasRelated = (id: number) =>
    (usersByRole.get(id)?.length ?? 0) > 0 ||
    (pagesByRole.get(id)?.length ?? 0) > 0 ||
    (jobsByRole.get(id)?.length ?? 0) > 0 ||
    (roles.find(r => r.role_id === id)?.tl.length ?? 0) > 0;

  const buildRelatedGroups = (id: number): RelatedChildGroup[] => [
    {
      key: 'users',
      table: 'csm_user_master', pkColumn: 'user_id',
      title: t('roles.list.related.users', 'Users'),
      items: (usersByRole.get(id) ?? []).map(u => ({ id: u.user_id, label: u.name, title: u.email })),
    },
    {
      key: 'pages',
      table: 'csm_page_master', pkColumn: 'page_id',
      title: t('roles.list.related.pages', 'Pages with access'),
      badgeTitles: PERM_TITLES,
      items: (pagesByRole.get(id) ?? []).map(p => ({ id: p.page_id, label: p.name, title: p.code, badges: p.perms })),
    },
    {
      key: 'jobs',
      table: 'csm_job_description', pkColumn: 'job_description_id',
      title: t('roles.list.related.job_descriptions', 'Job Descriptions'),
      items: (jobsByRole.get(id) ?? []).map(j => ({ id: j.job_id, label: j.name, title: j.code })),
    },
    buildTranslationsGroup(roles.find(r => r.role_id === id)?.tl, t, activeLocale),
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('roles.list.title', 'Roles')}
          description={t('roles.list.description', 'Security roles, the pages they can access, and the users who hold them.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('roles.list.new_role')}
              </button>)}
            </>
          }
        />

        {error && (
          <ErrorBanner
            message={t('roles.list.load_failed', "Couldn't load roles.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('roles.list.search_placeholder')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-csm-watermark focus:outline-none"
              />
            </div>
            <FacetFilter label={t('roles.list.columns.system')} options={facetOptions.system} selected={facets.system} onChange={v => setFacets(f => ({ ...f, system: v }))} />
            <FacetFilter label={t('roles.list.columns.active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ExtensionFacets ext={ext} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-watermark">{t('common.loading')}</span>}
              <PersonalizationToggle />
            </div>
          </div>

          {isPersonalizing && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1.5 text-csm-xs text-primary">
              {t('roles.list.personalization_banner', 'Personalization mode — configure columns below, then toggle off to save.')}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-auto">
        {busy && roles.length === 0 ? (
          <SkeletonRows rows={8} />
        ) : (
        <PersonalizedGrid componentKey="grid" columns={[...GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('roles.list.related.header', 'Related users, pages & job descriptions')}></th>
                  {visibleCols.map(col => {
                    if (col.key === 'internal_code') return <SortableHeader<RoleSortKey> key={col.key} sortKey="internal_code" label={t('roles.list.columns.internal_code')} currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} style={{ width: widths[col.key] }} />;
                    if (col.key === 'display_text')  return <SortableHeader<RoleSortKey> key={col.key} sortKey="display_text"  label={t('roles.list.columns.display_text')}  currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} style={{ width: widths[col.key] }} />;
                    if (col.key === 'is_system')     return <SortableHeader<RoleSortKey> key={col.key} sortKey="is_system"     label={t('roles.list.columns.system')}        currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} style={{ width: widths[col.key] }} />;
                    if (col.key === 'is_active')     return <SortableHeader<RoleSortKey> key={col.key} sortKey="is_active"     label={t('roles.list.columns.active')}        currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} style={{ width: widths[col.key] }} />;
                    if (udcGrid.isUdcKey(col.key))   return <SortableHeader<RoleSortKey> key={col.key} sortKey={col.key as RoleSortKey} label={col.header} currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} style={{ width: widths[col.key] }} />;
                    return null;
                  })}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('roles.list.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.role_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td
                      className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <RelatedExpandButton
                        open={detail.isOpen(r.role_id)}
                        hasChildren={hasRelated(r.role_id)}
                        onToggle={() => detail.toggle(r.role_id)}
                        expandLabel={t('roles.list.related.expand_aria', 'Show related records')}
                        collapseLabel={t('roles.list.related.collapse_aria', 'Hide related')}
                        tooltip={t('roles.list.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => {
                      if (col.key === 'internal_code') return <td key={col.key} style={{ width: widths[col.key] }} className="border-b border-csm-border-soft px-3 py-1.5 font-mono text-csm-sm">{r.internal_code}</td>;
                      if (col.key === 'display_text')  return <td key={col.key} style={{ width: widths[col.key] }} className="border-b border-csm-border-soft px-3 py-1.5">{r.display_text}</td>;
                      if (col.key === 'is_system')     return <td key={col.key} style={{ width: widths[col.key] }} className="border-b border-csm-border-soft px-3 py-1.5"><YesNoBadge value={r.is_system} yesLabel={t('common.boolean.system')} noLabel={t('common.boolean.custom')} /></td>;
                      if (col.key === 'is_active')     return <td key={col.key} style={{ width: widths[col.key] }} className="border-b border-csm-border-soft px-3 py-1.5"><YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active')} noLabel={t('common.boolean.inactive')} /></td>;
                      if (udcGrid.isUdcKey(col.key))   return <td key={col.key} style={{ width: widths[col.key] }} className="border-b border-csm-border-soft px-3 py-1.5">{udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}</td>;
                      return null;
                    })}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                      {canChange && (<button
                        onClick={() => setEditing(r)}
                        className="mr-1 inline-block min-w-[3.25rem] rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft"
                      >{t('roles.list.actions.edit')}</button>)}
                      {/* Change 4: open ConfirmModal instead of window.confirm */}
                      {canDelete && (<button
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}
                        disabled={r.is_system === 'Y'}
                        title={r.is_system === 'Y' ? t('roles.list.actions.delete_disabled_tooltip') : t('roles.list.actions.delete_tooltip')}
                        className="inline-block min-w-[3.75rem] rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:bg-csm-danger-soft disabled:cursor-not-allowed disabled:opacity-40"
                      >{t('roles.list.actions.delete')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.role_id) && (
                    <tr>
                      <td
                        colSpan={visibleCols.length + 2}
                        className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.role_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {/* Change 6: EmptyState component replaces inline italic message */}
                {filtered.length === 0 && !busy && (
                  <tr>
                    <td colSpan={visibleCols.length + 2} className="py-12 text-center">
                      <EmptyState
                        variant={anyFilterActive ? 'no-match' : 'no-data'}
                        title={filter ? t('roles.list.empty_filter') : t('roles.list.empty_no_filter')}
                        description={filter ? t('roles.list.empty_filter_desc', { filter }) : t('roles.list.empty_no_filter_desc')}
                        action={
                          filter
                            ? <button onClick={() => setFilter('')} className="mt-2 rounded border border-csm-border bg-white px-3 py-1.5 text-csm-sm text-csm-ink hover:bg-csm-surface-alt">{t('roles.list.empty_filter_clear')}</button>
                            : canAdd && (<button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('roles.list.new_role')}</button>)
                        }
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <RoleEditModal
          session={session}
          role={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }}
        />
      )}

      {/* Change 4: Delete confirmation modal */}
      {deleteTarget && (
        <ConfirmModal
          title={t('roles.list.delete_confirm_title')}
          message={t('roles.list.delete_confirm_message', { name: deleteTarget.display_text || deleteTarget.internal_code })}
          onConfirm={async () => { if (await handleDelete(deleteTarget)) setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
// EDIT MODAL
// =============================================================================

interface RoleEditModalProps {
  session: AuthSession;
  role: RoleRow | null;       // null = create mode
  onClose: () => void;
  onSaved: () => void;
}

type SimpleForm = { internal_code: string; is_active: 'Y' | 'N' };

function RoleEditModal({ session, role, onClose, onSaved }: RoleEditModalProps) {
  const { t } = useTranslation();
  // §8: page-access matrix labels are DISPLAYED in the active locale (the role's
  // own _tl rows below are loaded across ALL locales for the translations editor,
  // so those stay locale-agnostic).
  const activeLocale = useActiveLocale();
  const isCreate = role === null;

  // Change 3: dirty-state tracking via useDirtyForm for the simple scalar fields
  const makeInitial = (): SimpleForm => ({
    internal_code: role?.internal_code ?? '',
    is_active: role ? (role.is_active === 'Y' ? 'Y' : 'N') : 'Y',
  });
  const { values: form, setValues: setForm, isDirty: isSimpleDirty, reset: resetSimple } =
    useDirtyForm<SimpleForm>(makeInitial());

  const [translations, setTranslations] = useState<TranslationRow[]>([]);
  const [pages, setPages]               = useState<PageRow[]>([]);
  const [apps, setApps]                 = useState<AppInfo[]>([]);
  const [access, setAccess]             = useState<Map<number, AccessRow>>(new Map());
  const [collapsed, setCollapsed]       = useState<Set<number>>(new Set());
  // Application groups collapsed by default (external systems can carry thousands
  // of function rows — the platform's own app opens, the rest stay folded).
  const [collapsedApps, setCollapsedApps] = useState<Set<number>>(new Set());
  // Free-text filter over the access matrix (essential when an external system
  // contributes thousands of function rows).
  const [pageSearch, setPageSearch]     = useState('');
  const [busy, setBusy]                 = useState(false);
  const [error, setError]               = useState<string | null>(null);
  // Change 3: discard confirmation
  const [showDiscard, setShowDiscard]   = useState(false);
  // Change 5: internal_code validation error
  const [codeError, setCodeError]       = useState<string | null>(null);
  // Tracks dirty edits to translations/access (which are not covered by useDirtyForm).
  const hasEditedRef = useRef(false);

  // Custom fields placed on this entity (host = the list page, CSM.ROLE.LIST) become
  // first-class FieldRenderer items: they interleave with native fields and inherit
  // the same per-tier personalization (hide / read-only / required / reorder).
  const udcForm = useUdcFormFields('csm_role_master', 'CSM.ROLE.LIST', { pkColumn: 'role_id', pk: role?.role_id });
  // A derived app's 1:1 sister fields for this role (inert when none registered).
  const extForm = useRecordExtensionForm(PAGE_CODE, role?.role_id ?? null);
  const roleEditFieldDefs = useMemo(() => [...ROLE_EDIT_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const roleEditCoreLayout = useMemo(() => [...ROLE_EDIT_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const { layout: roleEditLayout } = useUldLayout('CSM.ROLE.EDIT.MODAL', roleEditCoreLayout);

  // Convenience accessors
  const internalCode = form.internal_code;
  const isActive = form.is_active === 'Y';
  // isDirty covers ALL mutable areas: scalar fields + translations + access matrix.
  const isDirty = isSimpleDirty || hasEditedRef.current;

  // Reset simple form when role prop changes (modal re-opens for a different role)
  useEffect(() => {
    resetSimple(makeInitial());
    setCodeError(null);
    hasEditedRef.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Hydrate child data: page list, existing translations, existing access.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const locale = activeLocale;

      // Load active pages with their localized labels.
      const { data: pageData, error: pErr } = await apiDb
        .from('csm_page_master')
        .select(`
          page_id, internal_code, sort_order, parent_page_id, application_id, route_path,
          csm_page_master_tl ( locale_code, display_text )
        `)
        .eq('is_active', 'Y')
        .order('sort_order');
      if (pErr) { setError(pErr.message); return; }

      type PageDataRow = {
        page_id: number;
        internal_code: string;
        parent_page_id: number | null;
        sort_order: number | null;
        application_id: number | null;
        route_path: string | null;
        csm_page_master_tl?: TlRow[];
      };
      const pageRows: PageRow[] = (pageData ?? []).map((p: PageDataRow) => {
        const tls: { locale_code: string; display_text: string }[] = p.csm_page_master_tl ?? [];
        const exact = tls.find(t => t.locale_code === locale);
        const fallback = tls.find(t => t.locale_code === 'EN_US') ?? tls[0];
        return {
          page_id: p.page_id,
          internal_code: p.internal_code,
          display_text: exact?.display_text ?? fallback?.display_text ?? p.internal_code,
          parent_page_id: p.parent_page_id ?? null,
          sort_order: p.sort_order ?? 0,
          application_id: p.application_id ?? null,
          route_path: p.route_path ?? null,
        };
      });
      if (cancelled) return;
      setPages(pageRows);
      // Start collapsed: only module-level groups visible, child pages hidden.
      setCollapsed(new Set(
        pageRows.filter(p => pageRows.some(c => c.parent_page_id === p.page_id)).map(p => p.page_id)
      ));

      // Applications, so the matrix can group by system (default app first). Only
      // surfaces a grouping level when more than one app has pages, so a
      // single-application platform (BaseApp itself) renders exactly as before.
      const { data: appData } = await apiDb
        .from('csm_application_master')
        .select('application_id, internal_code, is_default, csm_application_master_tl ( locale_code, display_text )');
      if (cancelled) return;
      type RawApp = { application_id: number; internal_code: string; is_default: 'Y' | 'N'; csm_application_master_tl?: TlRow[] };
      const appInfos: AppInfo[] = (appData ?? []).map((a: RawApp) => {
        const tls = a.csm_application_master_tl ?? [];
        const exact = tls.find(t => t.locale_code === locale);
        const fallback = tls.find(t => t.locale_code === 'EN_US') ?? tls[0];
        return { application_id: a.application_id, label: exact?.display_text ?? fallback?.display_text ?? a.internal_code, is_default: a.is_default === 'Y' };
      });
      setApps(appInfos);
      // Fold every non-default application by default (external function catalogs
      // are large); the platform's own app stays open.
      setCollapsedApps(new Set(appInfos.filter(a => !a.is_default).map(a => a.application_id)));

      // Existing translations + access (only when editing).
      if (!isCreate && role) {
        const { data: tlRows } = await apiDb
          .from('csm_role_master_tl')
          .select('locale_code, display_text, description')
          .eq('role_id', role.role_id);
        if (cancelled) return;
        const existing: TranslationRow[] = (tlRows ?? []).map((t: TlRow) => ({
          locale_code: t.locale_code,
          display_text: t.display_text ?? '',
          description: t.description ?? '',
        }));
        setTranslations(existing);

        const { data: rpaRows } = await apiDb
          .from('csm_role_page_access')
          .select('page_id, can_view, can_add, can_change, can_delete, can_signoff, can_personalize_role, can_personalize_user')
          .eq('role_id', role.role_id);
        if (cancelled) return;
        const map = new Map<number, AccessRow>();
        for (const r of (rpaRows ?? []) as AccessRow[]) map.set(r.page_id, r);
        setAccess(map);
      }
    })();
    return () => { cancelled = true; };
  }, [isCreate, role, activeLocale]);

  const setAccessFlag = (pageId: number, key: keyof AccessRow, val: boolean) => {
    hasEditedRef.current = true;
    setAccess(prev => {
      const next = new Map(prev);
      const current = next.get(pageId) ?? {
        page_id: pageId, can_view: false, can_add: false, can_change: false, can_delete: false, can_signoff: false, can_personalize_role: false, can_personalize_user: false,
      };
      next.set(pageId, { ...current, [key]: val });
      return next;
    });
  };

  const editTranslations = (updater: (prev: TranslationRow[]) => TranslationRow[]) => {
    hasEditedRef.current = true;
    setTranslations(updater);
  };

  // ── Hierarchy (mirrors the Pages menu via parent_page_id) ───────────────────
  const pageTree = useMemo(() => buildPageTree(pages), [pages]);

  const allGroupIds = useMemo(() => {
    const ids = new Set<number>();
    const walk = (ns: PageNode[]) => ns.forEach(n => { if (n.children.length) { ids.add(n.page.page_id); walk(n.children); } });
    walk(pageTree);
    return ids;
  }, [pageTree]);

  const pageById = useMemo(() => new Map(pages.map(p => [p.page_id, p] as const)), [pages]);

  // Group the top-level pages by application (the platform's own app first, then
  // by label). Pages whose application is unknown fall under the default app. A
  // grouping header only appears when more than one application has pages, so a
  // single-application platform (BaseApp itself) renders exactly as before.
  const defaultAppId = useMemo(() => apps.find(a => a.is_default)?.application_id ?? null, [apps]);
  const appGroups = useMemo(() => {
    const appById = new Map(apps.map(a => [a.application_id, a] as const));
    const buckets = new Map<number | null, PageNode[]>();
    for (const root of pageTree) {
      const aid = root.page.application_id ?? defaultAppId;
      const key = (aid != null && appById.has(aid)) ? aid : defaultAppId;
      const arr = buckets.get(key) ?? [];
      arr.push(root);
      buckets.set(key, arr);
    }
    const groups = Array.from(buckets.entries()).map(([appId, roots]) => ({
      appId, app: appId != null ? appById.get(appId) ?? null : null, roots,
    }));
    groups.sort((a, b) => {
      const ad = a.app?.is_default ? 0 : 1, bd = b.app?.is_default ? 0 : 1;
      return ad !== bd ? ad - bd : (a.app?.label ?? '').localeCompare(b.app?.label ?? '');
    });
    return groups;
  }, [pageTree, apps, defaultAppId]);
  const multiApp = appGroups.length > 1;

  // Search filter over the access matrix: a node is shown when it (or a
  // descendant) matches, and matching forces groups open regardless of collapse.
  const search = pageSearch.trim().toLowerCase();
  const nodeMatches = (p: PageRow) =>
    !search || p.display_text.toLowerCase().includes(search) || p.internal_code.toLowerCase().includes(search);
  const subtreeMatches = (node: PageNode): boolean =>
    nodeMatches(node.page) || node.children.some(subtreeMatches);

  // flattened, in display order, respecting collapse state and the search filter
  const flattenVisible = (roots: PageNode[]): { node: PageNode; depth: number; isGroup: boolean }[] => {
    const out: { node: PageNode; depth: number; isGroup: boolean }[] = [];
    const walk = (nodes: PageNode[], depth: number) => {
      for (const n of nodes) {
        if (search && !subtreeMatches(n)) continue;
        const isGroup = n.children.length > 0;
        out.push({ node: n, depth, isGroup });
        if (isGroup && (search || !collapsed.has(n.page.page_id))) walk(n.children, depth + 1);
      }
    };
    walk(roots, 0);
    return out;
  };

  const toggleCollapse = (pageId: number) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(pageId)) next.delete(pageId); else next.add(pageId);
    return next;
  });

  const toggleCollapseApp = (appId: number | null) => setCollapsedApps(prev => {
    const key = appId ?? -1;
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const flagOn = (pageId: number, key: FlagKey) => !!access.get(pageId)?.[key];

  // tri-state of a group column = state across the whole subtree (incl. the group node)
  const groupState = (node: PageNode, key: FlagKey): 'all' | 'some' | 'none' => {
    const ids = subtreeIds(node);
    const on = ids.filter(id => flagOn(id, key)).length;
    return on === 0 ? 'none' : on === ids.length ? 'all' : 'some';
  };

  // cascade a flag to the group node + every descendant (so the menu nests correctly)
  const setSubtreeFlag = (node: PageNode, key: FlagKey, val: boolean) => {
    hasEditedRef.current = true;
    const ids = subtreeIds(node);
    setAccess(prev => {
      const next = new Map(prev);
      for (const id of ids) {
        const cur = next.get(id) ?? { page_id: id, can_view: false, can_add: false, can_change: false, can_delete: false, can_signoff: false, can_personalize_role: false, can_personalize_user: false };
        next.set(id, { ...cur, [key]: val });
      }
      return next;
    });
  };

  // Change 3: intercept modal close with dirty check (covers all mutable areas)
  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  // Prevent the outer Modal's Escape listener from firing when the discard
  // ConfirmModal is open. Both are mounted simultaneously and Modal.tsx uses a
  // document-scoped non-capture listener; we register a capture-phase listener
  // that calls preventDefault() so the outer modal's handler sees
  // e.defaultPrevented and skips.
  useEffect(() => {
    if (!showDiscard) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') e.preventDefault(); };
    document.addEventListener('keydown', h, true);
    return () => document.removeEventListener('keydown', h, true);
  }, [showDiscard]);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      let roleId = role?.role_id;

      // 1. Upsert csm_role_master.
      if (isCreate) {
        const { row_id, error: e } = await routedInsert(session, 'csm_role_master', {
          internal_code: internalCode,
          is_system: 'N',
          is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw e;
        roleId = row_id ?? undefined;
      } else {
        const { error: e } = await routedUpdate(session, 'csm_role_master', 'role_id', role?.role_id, {
          is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw e;
      }

      // 2. Replace translations (idempotent upsert per row).
      for (const t of translations) {
        if (!t.locale_code || !t.display_text) continue;
        const { error: e } = await apiDb
          .from('csm_role_master_tl')
          .upsert({
            role_id: roleId,
            locale_code: t.locale_code,
            display_text: t.display_text,
            description: t.description || null,
          }, { onConflict: 'role_id,locale_code' });
        if (e) throw e;
      }

      // 3. Replace csm_role_page_access matrix.
      //    Wipe then re-insert any rows that have at least one TRUE flag.
      //    Multi-row-by-filter has no single-routine form (§2): resolve the
      //    existing PKs for this role, then route one DELETE per row.
      const { data: existingRpa, error: dFindErr } = await apiDb
        .from('csm_role_page_access')
        .select('role_page_access_id')
        .eq('role_id', roleId);
      if (dFindErr) throw dFindErr;
      for (const rpa of (existingRpa ?? []) as { role_page_access_id: number }[]) {
        const { error: dErr } = await routedDelete(session, 'csm_role_page_access', 'role_page_access_id', rpa.role_page_access_id);
        if (dErr) throw dErr;
      }

      // A viewable page implies its ancestor groups are viewable, otherwise the
      // navigation menu (which nests only can_view nodes) would orphan it.
      const parentOf = new Map<number, number | null>();
      for (const p of pages) parentOf.set(p.page_id, p.parent_page_id);
      const effective = new Map(access);
      for (const a of access.values()) {
        if (!a.can_view) continue;
        let pid = parentOf.get(a.page_id) ?? null;
        while (pid != null) {
          const cur = effective.get(pid) ?? { page_id: pid, can_view: false, can_add: false, can_change: false, can_delete: false, can_signoff: false, can_personalize_role: false, can_personalize_user: false };
          if (!cur.can_view) effective.set(pid, { ...cur, can_view: true });
          pid = parentOf.get(pid) ?? null;
        }
      }

      const inserts = Array.from(effective.values())
        .filter(a => a.can_view || a.can_add || a.can_change || a.can_delete || a.can_signoff || a.can_personalize_role || a.can_personalize_user)
        .map(a => {
          // A function (no route) is atomic — persist only its granted/view flag,
          // never the CRUD flags a group-cascade may have set across it.
          const pg = pageById.get(a.page_id);
          if (pg && isFunctionPage(pg)) {
            return { role_id: roleId, page_id: a.page_id, can_view: a.can_view,
              can_add: false, can_change: false, can_delete: false, can_signoff: false,
              can_personalize_role: false, can_personalize_user: false };
          }
          return { role_id: roleId, ...a };
        })
        .filter(a => a.can_view || a.can_add || a.can_change || a.can_delete || a.can_signoff || a.can_personalize_role || a.can_personalize_user);
      if (inserts.length > 0) {
        const { error: iErr } = await routedInsertMany(session, 'csm_role_page_access', inserts);
        if (iErr) throw iErr;
      }

      // 4. A derived app's sister-table fields (runs after the host row exists so
      //    the sister can key off role_id; inert when no extension is registered).
      if (roleId != null) await extForm.save(session, roleId);

      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Change 2: modal title format (from locale keys, consistent with task spec)
  const modalTitle = isCreate
    ? t('roles.drawer.title_create')
    : t('roles.drawer.title_edit', { code: role?.internal_code });

  return (
    <>
      <Modal
        title={modalTitle}
        onClose={handleClose}
        width="2xl"
        headerAction={<><PersonalizationToggle /></>}
        footer={
          <>
            <button
              onClick={handleClose}
              className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft"
            >{t('common.cancel')}</button>
            <button
              onClick={() => void save()}
              disabled={busy || (isCreate && !internalCode)}
              className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60"
            >{t(busy ? 'common.saving' : 'common.save')}</button>
          </>
        }
      >
        <div>
            {/* Code + active */}
            <div className="mb-4">
              <FieldRenderer
                fields={roleEditFieldDefs}
                layout={roleEditLayout}
                columns={2}
                pageCode="CSM.ROLE.EDIT.MODAL"
                session={session}
                render={(f) => {
                  if (f.key === 'internal_code') return (
                    <PersonalizedField componentKey="drawer" fieldKey="internal_code" label={t('roles.drawer.field.internal_code')}>
                      <div>
                        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('roles.drawer.field.internal_code')}</label>
                        <input
                          type="text"
                          value={internalCode}
                          disabled={!isCreate}
                          onChange={e => {
                            const v = e.target.value.toUpperCase();
                            setForm((f: SimpleForm) => ({ ...f, internal_code: v }));
                            if (codeError) setCodeError(null);
                          }}
                          onBlur={() => {
                            // Change 5: validate format on blur (create mode only)
                            if (isCreate && internalCode && !/^[A-Z][A-Z0-9_.]*$/.test(internalCode)) {
                              setCodeError(t('roles.drawer.field.internal_code_format_error', 'Must start with a letter — uppercase letters, numbers, dots only.'));
                            }
                          }}
                          placeholder={t('roles.drawer.field.internal_code_placeholder')}
                          className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted"
                        />
                        {/* Change 5: validation error or always-visible helper text */}
                        {codeError
                          ? <p className="text-csm-xs text-red-600 mt-1">{codeError}</p>
                          : <p className="text-csm-xs text-csm-ink-soft mt-1">{t('roles.drawer.field.internal_code_hint', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>
                        }
                      </div>
                    </PersonalizedField>
                  );
                  if (f.key === 'is_active') return (
                    <PersonalizedField componentKey="drawer" fieldKey="is_active" label={t('roles.drawer.field.active_checkbox')}>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft">
                          <input
                            type="checkbox"
                            checked={isActive}
                            onChange={e => setForm((f: SimpleForm) => ({ ...f, is_active: e.target.checked ? 'Y' : 'N' }))}
                            disabled={role?.is_system === 'Y'}
                            className="h-4 w-4 accent-gray-700"
                          />
                          {t('roles.drawer.field.active_checkbox')}
                        </label>
                      </div>
                    </PersonalizedField>
                  );
                  if (udcForm.isUdcKey(f.key)) return (
                    <PersonalizedField componentKey="drawer" fieldKey={f.key} label={f.label}
                      readonlyChildren={udcForm.renderControl(f.key, { disabled: true })}>
                      {udcForm.renderControl(f.key)}
                    </PersonalizedField>
                  );
                  return null;
                }}
              />
            </div>

            {/* Translations */}
            <div className="mb-4">
              <TranslationsEditor
                value={translations}
                onChange={(rows) => editTranslations(() => rows.map(r => ({ locale_code: r.locale_code, display_text: r.display_text, description: r.description ?? '' })))}
                // Seeding empty locale rows is not an edit. Routed through
                // onChange it set hasEditedRef on mount, so opening this form —
                // including as a child drill-down — reported unsaved changes
                // before the user had touched anything.
                onSeed={(rows) => setTranslations(rows.map(r => ({ locale_code: r.locale_code, display_text: r.display_text, description: r.description ?? '' })))}
                showDescription
                label={t('roles.drawer.translations.label')}
              />
            </div>

            {/* Derived-app sister-table fields (inert when no extension registered). */}
            <RecordExtensionFields form={extForm} componentKey="drawer" />

            {/* Page access matrix - grouped identically to the Pages menu (parent_page_id) */}
            <div className="mb-2">
              <div className="mb-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-csm-xs text-amber-800">
                {t('roles.drawer.access.admin_bypass_notice', 'Users with Admin Access (is_admin = Y) bypass this matrix and have full access to all pages automatically. This matrix only applies to non-admin users assigned to this role.')}
              </div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <label className="block text-csm-sm font-medium text-csm-ink-soft">{t('roles.drawer.access.label', 'Page Access')}</label>
                {allGroupIds.size > 0 && (
                  <div className="flex gap-2 text-csm-2xs">
                    <button type="button" onClick={() => { setCollapsed(new Set()); setCollapsedApps(new Set()); }} className="text-primary hover:underline">{t('roles.drawer.access.expand_all', 'Expand all')}</button>
                    <span className="text-csm-border">|</span>
                    <button type="button" onClick={() => { setCollapsed(new Set(allGroupIds)); setCollapsedApps(new Set(apps.filter(a => !a.is_default).map(a => a.application_id))); }} className="text-primary hover:underline">{t('roles.drawer.access.collapse_all', 'Collapse all')}</button>
                  </div>
                )}
              </div>
              {/* Search — essential when an external system contributes thousands of function rows. */}
              {pages.length > 12 && (
                <div className="mb-1 flex items-center gap-1.5 rounded border border-csm-border bg-white px-2">
                  <Search className="h-3.5 w-3.5 shrink-0 text-csm-watermark" />
                  <input
                    type="text"
                    value={pageSearch}
                    onChange={e => setPageSearch(e.target.value)}
                    placeholder={t('roles.drawer.access.search_placeholder', 'Search pages / functions…')}
                    className="h-7 w-full border-0 bg-transparent text-csm-sm focus:outline-none"
                  />
                </div>
              )}
              <div className="overflow-hidden rounded border border-csm-border">
                <table className="w-full text-csm-sm">
                  <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                    <tr>
                      <th className="px-2 py-1 text-left">{t('roles.drawer.access.col.page', 'Page')}</th>
                      <th className="px-2 py-1 text-center">{t('roles.drawer.access.col.view', 'View')}</th>
                      <th className="px-2 py-1 text-center">{t('roles.drawer.access.col.add', 'Add')}</th>
                      <th className="px-2 py-1 text-center">{t('roles.drawer.access.col.change', 'Change')}</th>
                      <th className="px-2 py-1 text-center">{t('roles.drawer.access.col.delete', 'Delete')}</th>
                      <th className="px-2 py-1 text-center">{t('roles.drawer.access.col.signoff', 'Sign-off')}</th>
                      <th className="px-2 py-1 text-center">{t('roles.drawer.access.col.role_design', 'Role Design')}</th>
                      <th className="px-2 py-1 text-center">{t('roles.drawer.access.col.user_personalize', 'User Personalize')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appGroups.map(group => {
                      const rows = flattenVisible(group.roots);
                      if (search && rows.length === 0) return null;
                      const appKey = group.appId ?? -1;
                      const appCollapsed = !search && multiApp && collapsedApps.has(appKey);
                      const leafCount = group.roots.reduce((n, r) => n + subtreeIds(r).length, 0);
                      return (
                        <Fragment key={`app-${appKey}`}>
                          {multiApp && (
                            <tr className="border-t-2 border-csm-border bg-csm-border-soft">
                              <td colSpan={8} className="px-2 py-1">
                                <button type="button" onClick={() => toggleCollapseApp(group.appId)} className="flex items-center gap-1 text-left font-bold text-csm-ink">
                                  <svg className={`h-3 w-3 shrink-0 transition-transform ${appCollapsed ? '' : 'rotate-90'}`} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 2l4 4-4 4"/></svg>
                                  <span>{group.app?.label ?? t('roles.drawer.access.other_app', 'Other')}</span>
                                  <span className="text-csm-2xs font-normal text-csm-watermark">({leafCount})</span>
                                </button>
                              </td>
                            </tr>
                          )}
                          {!appCollapsed && rows.map(({ node, depth, isGroup }) => {
                            const p = node.page;
                            const indent = { paddingLeft: 8 + (depth + (multiApp ? 1 : 0)) * 16 };
                            if (isGroup) {
                              const isCollapsed = !search && collapsed.has(p.page_id);
                              return (
                                <tr key={p.page_id} className="border-t border-csm-border bg-csm-surface-alt">
                                  <td className="px-2 py-1">
                                    <button type="button" onClick={() => toggleCollapse(p.page_id)} className="flex items-center gap-1 text-left font-semibold text-csm-ink" style={indent}>
                                      <svg className={`h-3 w-3 shrink-0 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 2l4 4-4 4"/></svg>
                                      <span>{p.display_text}</span>
                                    </button>
                                  </td>
                                  {FLAG_KEYS.map(k => {
                                    const st = groupState(node, k);
                                    return (
                                      <td key={k} className="px-1 py-1 text-center">
                                        <input type="checkbox" ref={el => { if (el) el.indeterminate = st === 'some'; }} checked={st === 'all'} onChange={e => setSubtreeFlag(node, k, e.target.checked)} className="h-3.5 w-3.5 accent-gray-700" title={t('roles.drawer.access.group_apply_tooltip', 'Apply to this group and all pages under it')} />
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            }
                            const a = access.get(p.page_id);
                            // A function (no route) is atomic: it grants on/off (View only).
                            const fn = isFunctionPage(p);
                            return (
                              <tr key={p.page_id} className="border-t border-csm-border-soft">
                                <td className="px-2 py-1">
                                  <div className="border-l-2 border-csm-border ml-2" style={indent}>
                                    <div className="pl-2">{p.display_text}</div>
                                    <div className="pl-2 font-mono text-csm-2xs text-csm-watermark">{p.internal_code}</div>
                                  </div>
                                </td>
                                {FLAG_KEYS.map(k => (
                                  <td key={k} className="px-1 py-1 text-center">
                                    {(!fn || k === 'can_view')
                                      ? <input type="checkbox" checked={!!a?.[k]} onChange={e => setAccessFlag(p.page_id, k, e.target.checked)} className="h-3.5 w-3.5 accent-gray-700" />
                                      : <span className="text-csm-2xs text-csm-border">—</span>}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                    {pages.length === 0 && (
                      <tr><td colSpan={8} className="px-2 py-3 text-center italic text-csm-watermark">
                        {t('roles.drawer.access.no_pages', 'No active pages registered yet. Run page sync first.')}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {error && (
              <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">
                {error}
              </div>
            )}
        </div>
      </Modal>

      {/* Change 3: Discard-changes confirmation dialog */}
      {showDiscard && (
        <ConfirmModal
          title={t('roles.drawer.discard_title')}
          message={t('roles.drawer.discard_message')}
          confirmLabel={t('roles.drawer.discard_confirm')}
          cancelLabel={t('roles.drawer.discard_cancel')}
          danger={false}
          onConfirm={() => { resetSimple(); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// §17.B13 drill-down — this page OWNS the role editor, so it registers it for
// every other page's related-children panel. Registration is a module-load
// side effect (page_components.tsx imports every page statically), so nothing
// is exported and no page imports another: Users -> Roles -> Users would
// otherwise be an import cycle.
// ---------------------------------------------------------------------------
function RoleChildEditor({ session, pk, onClose, onSaved }: ChildEditorProps) {
  const { t } = useTranslation();
  const [row, setRow] = useState<RoleRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error: e } = await apiDb
        .from('csm_role_master')
        .select('role_id, internal_code, is_system, is_active')
        .eq('role_id', pk)
        .maybeSingle();
      if (cancelled) return;
      if (e) setError(e.message);
      else if (data) {
        const r = data as Omit<RoleRow, 'display_text'>;
        setRow({ ...r, display_text: r.internal_code });
      }
      setBusy(false);
    })();
    return () => { cancelled = true; };
  }, [pk]);

  if (busy || error || !row) {
    return (
      <Modal title={t('roles.edit_title', 'Role')} onClose={onClose} width="lg">
        <div className={'px-1 py-3 text-csm-sm ' + (error ? 'text-csm-danger' : 'text-csm-muted')}>
          {error ?? (busy ? t('common.loading', 'Loading…') : t('common.not_found', 'Record not found.'))}
        </div>
      </Modal>
    );
  }
  return <RoleEditModal session={session} role={row} onClose={onClose} onSaved={onSaved} />;
}

registerChildEditor('csm_role_master', {
  pageCode: PAGE_CODE,
  title: 'Role',
  Editor: RoleChildEditor,
});
